import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { API } from '../config/api';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setIsSupported(supported);
    if (supported) {
      setPermission(Notification.permission);
      checkSubscription();
    }
  }, []);

  const checkSubscription = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        // The browser can keep a subscription the server no longer has (created on another
        // server, or with different keys): show "Attive" only if it is valid and saved there.
        const { data } = await axios.get(`${API}/push/vapid-key`);
        const current = urlBase64ToUint8Array(data.public_key);
        const used = subscription.options?.applicationServerKey
          ? new Uint8Array(subscription.options.applicationServerKey) : null;
        const sameKey = used && used.length === current.length && used.every((b, i) => b === current[i]);
        if (!sameKey) {
          await subscription.unsubscribe();
          subscription = null;
        } else {
          await axios.post(`${API}/push/subscribe`, { subscription: subscription.toJSON() });
        }
      }
      setIsSubscribed(!!subscription);
    } catch (e) {
      console.error('Error checking push subscription:', e);
    }
  }, []);

  const subscribe = useCallback(async () => {
    if (!isSupported) return false;
    setLoading(true);

    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setLoading(false);
        return false;
      }

      const { data } = await axios.get(`${API}/push/vapid-key`);
      const vapidKey = data.public_key;

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      await axios.post(`${API}/push/subscribe`, {
        subscription: subscription.toJSON()
      });

      setIsSubscribed(true);
      setLoading(false);
      return true;
    } catch (e) {
      console.error('Push subscribe error:', e);
      setLoading(false);
      return false;
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await axios.delete(`${API}/push/subscribe`, {
          data: { endpoint: subscription.endpoint }
        });
        await subscription.unsubscribe();
      }
      setIsSubscribed(false);
    } catch (e) {
      console.error('Push unsubscribe error:', e);
    }
    setLoading(false);
  }, []);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribe,
    unsubscribe,
    checkSubscription
  };
}
